

// ------------------------------------OFFRE WITH  QUOTA ANNUEL --------------------------------
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, Button, Form, Spinner, Row, Col, Badge } from "react-bootstrap";
import { createOffer, updateOffer, Offer } from "../services/offreService";
import { toast } from "react-toastify";
import "./styles/ModalOffre.css";

interface ModalOffreProps {
  show: boolean;
  onClose: () => void;
  onCreated?: (offre: Offer) => void;
  existingOffer?: Offer | null;
}

const ModalOffre: React.FC<ModalOffreProps> = ({ 
  show, 
  onClose, 
  onCreated, 
  existingOffer 
}) => {
  // États
  const [nom, setNom] = useState("");
  const [emailsParJour, setEmailsParJour] = useState<number>(0);
  const [emailsParMois, setEmailsParMois] = useState<number>(0);
  const [emailsParAn, setEmailsParAn] = useState<number>(0);
  const [monthlyPrice, setMonthlyPrice] = useState<number>(0);
  const [yearlyPrice, setYearlyPrice] = useState<number>(0);
  const [description, setDescription] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Calculs dérivés
  const coutParEmail = useMemo(() => {
    if (monthlyPrice > 0 && emailsParMois > 0) {
      return monthlyPrice / emailsParMois;
    }
    return 0;
  }, [monthlyPrice, emailsParMois]);

  const coutParEmailAnuel = useMemo(() => {
    if (yearlyPrice > 0 && emailsParAn > 0) {
      return yearlyPrice / emailsParAn;
    }
    return 0;
  }, [yearlyPrice, emailsParAn]);

  // Effets pour la synchronisation des quotas
  useEffect(() => {
    if (emailsParJour > 0 && emailsParMois === 0) {
      setEmailsParMois(emailsParJour * 30);
    }
  }, [emailsParJour, emailsParMois]);

  useEffect(() => {
    if (emailsParMois > 0 && emailsParAn === 0) {
      setEmailsParAn(emailsParMois * 12);
    }
  }, [emailsParMois, emailsParAn]);

  // Initialisation des données existantes
  const resetForm = useCallback(() => {
    setNom("");
    setEmailsParJour(0);
    setEmailsParMois(0);
    setEmailsParAn(0);
    setMonthlyPrice(0);
    setYearlyPrice(0);
    setDescription("");
    setErrors({});
  }, []);

  useEffect(() => {
    if (existingOffer && show) {
      setNom(existingOffer.name || "");
      setEmailsParJour(existingOffer.daily_emails || 0);
      setEmailsParMois(existingOffer.monthly_emails || 0);
      setEmailsParAn(existingOffer.yearly_emails || 0);
      setMonthlyPrice(existingOffer.monthly_price || 0);
      setYearlyPrice(existingOffer.yearly_price || 0);
      setDescription(existingOffer.description || "");
    } else if (show) {
      resetForm();
    }
  }, [existingOffer, show, resetForm]);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!nom.trim()) {
      newErrors.nom = "Le nom de l'offre est requis";
    }

    if (emailsParJour <= 0) {
      newErrors.emailsParJour = "Le quota journalier doit être positif";
    }

    if (emailsParMois <= 0) {
      newErrors.emailsParMois = "Le quota mensuel doit être positif";
    }

    if (monthlyPrice < 0) {
      newErrors.monthlyPrice = "Le prix mensuel ne peut pas être négatif";
    }

    if (yearlyPrice < 0) {
      newErrors.yearlyPrice = "Le prix annuel ne peut pas être négatif";
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

const handleSubmit = async () => {
  if (!validateForm()) {
    toast.error("Veuillez corriger les erreurs dans le formulaire");
    return;
  }

  setLoading(true);
  try {

    const offerData = {
      name: nom.trim(),
      daily_emails: emailsParJour,
      monthly_emails: emailsParMois,
      yearly_emails: emailsParAn,
      monthly_price: monthlyPrice,
      yearly_price: yearlyPrice,
      description: description.trim(),
    };



    let result;

    console.log("Données envoyées :", offerData);

    if (existingOffer?.id) {
      result = await updateOffer(existingOffer.id, offerData);
      toast.success(`Offre "${nom}" mise à jour avec succès !`);
    } else {
      result = await createOffer(offerData);
      toast.success(`Offre "${nom}" créée avec succès !`);
    }

    if (onCreated) {
      onCreated(result);
    }
    onClose();
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || error.message || "Erreur lors de l'opération";
    toast.error(errorMessage);
    console.error("Erreur:", error);
  } finally {
    setLoading(false);
  }
};


  // Gestionnaires d'événements
  const handleNomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNom(e.target.value);
    if (errors.nom) setErrors(prev => ({ ...prev, nom: "" }));
  };

  const handleEmailsJourChange = (value: number) => {
    setEmailsParJour(value);
    if (errors.emailsParJour) setErrors(prev => ({ ...prev, emailsParJour: "" }));
  };

  const handleEmailsMoisChange = (value: number) => {
    setEmailsParMois(value);
    if (errors.emailsParMois) setErrors(prev => ({ ...prev, emailsParMois: "" }));
  };

  const handleMonthlyPriceChange = (value: number) => {
    setMonthlyPrice(value);
    if (errors.monthlyPrice) setErrors(prev => ({ ...prev, monthlyPrice: "" }));
  };

  const handleYearlyPriceChange = (value: number) => {
    setYearlyPrice(value);
    if (errors.yearlyPrice) setErrors(prev => ({ ...prev, yearlyPrice: "" }));
  };

const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
  const value = e.target.value;

  if (value.length <= 250) {
    setDescription(value);
  }
};


  // Calcul du prix mensuel équivalent annuel
  const monthlyEquivalent = useMemo(() => {
    if (yearlyPrice > 0) {
      return yearlyPrice / 12;
    }
    return 0;
  }, [yearlyPrice]);

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
      className="modal-offre-modern"
      scrollable
    >
      <Modal.Header closeButton className="bg-gradient-primary text-white border-0">
        <div className="d-flex align-items-center w-100">
          <div className="modal-icon me-3">
            <i className="bi bi-envelope-plus fs-3"></i>
          </div>
          <div className="flex-grow-1">
            <Modal.Title className="fw-bold mb-1">
              {existingOffer ? "Modifier l'offre" : "Nouvelle offre d'emails"}
            </Modal.Title>
            <p className="mb-0 opacity-75" style={{ fontSize: "0.9rem" }}>
              {existingOffer 
                ? "Mettez à jour les détails de votre offre" 
                : "Configurez une nouvelle offre commerciale"
              }
            </p>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className="py-4 px-4 px-md-5 bg-light">
        {errors.coherence && (
          <div className="alert alert-warning alert-dismissible fade show" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {errors.coherence}
            <button 
             title ="Fermer"
              type="button" 
              className="btn-close" 
              onClick={() => setErrors(prev => ({ ...prev, coherence: "" }))}
            ></button>
          </div>
        )}

        <Form>
          {/* Nom de l'offre */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold d-flex align-items-center">
              <i className="bi bi-tag me-2 text-primary"></i>
              Nom de l'offre *
            </Form.Label>
            <div className="input-group has-validation">
              <span className="input-group-text bg-white border-end-0">
                <i className="bi bi-fonts text-muted"></i>
              </span>
              <Form.Control
                type="text"
                value={nom}
                onChange={handleNomChange}
                placeholder="Ex: Pack Premium, Basique, Entreprise..."
                className={`border-start-0 ps-0 py-3 ${errors.nom ? 'is-invalid' : ''}`}
                style={{ fontSize: "1.05rem" }}
                required
              />
              {errors.nom && (
                <div className="invalid-feedback d-flex align-items-center">
                  <i className="bi bi-exclamation-circle me-2"></i>
                  {errors.nom}
                </div>
              )}
            </div>
            <Form.Text className="text-muted d-flex align-items-center mt-2">
              <i className="bi bi-lightbulb me-2"></i>
              Choisissez un nom clair pour identifier cette offre
            </Form.Text>
          </Form.Group>

          {/* Quotas d'emails */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-3 d-flex align-items-center">
              <i className="bi bi-envelope me-2" style={{ color: "#4BB0C0" }}></i>
              Quotas d'emails *
            </h6>
            <Row>
              {/* Emails par jour */}
              <Col md={6} className="mb-3">
                <div className="card h-100 border-0 shadow-sm hover-shadow">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <div className="icon-container bg-info bg-opacity-10 p-2 rounded-circle me-3">
                        <i className="bi bi-sun text-info fs-5"></i>
                      </div>
                      <div>
                        <Form.Label className="fw-semibold mb-1">Emails par jour *</Form.Label>
                        <small className="text-muted d-block">Quota journalier</small>
                      </div>
                    </div>
                    <div className="input-group has-validation">
                      <Form.Control
                        type="number"
                        min="1"
                        step="1"
                        value={emailsParJour || ""}
                        onChange={(e) => handleEmailsJourChange(Number(e.target.value) || 0)}
                        placeholder="Ex: 100"
                        className={`py-2 ${errors.emailsParJour ? 'is-invalid' : ''}`}
                      />
                      <span className="input-group-text bg-light">/jour</span>
                      {errors.emailsParJour && (
                        <div className="invalid-feedback">
                          {errors.emailsParJour}
                        </div>
                      )}
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <small className="text-muted">
                        <i className="bi bi-calendar-day me-1"></i>
                        Quotidien
                      </small>
                      <Badge bg="info" className="px-2">
                        {emailsParJour.toLocaleString()} email(s)
                      </Badge>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Emails par mois */}
              <Col md={6} className="mb-3">
                <div className="card h-100 border-0 shadow-sm hover-shadow">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <div className="icon-container bg-opacity-10 p-2 rounded-circle me-3" style={{ backgroundColor: "rgba(75, 176, 192, 0.1)" }}>
                        <i className="bi bi-calendar-month fs-5" style={{ color: "#4BB0C0" }}></i>
                      </div>
                      <div>
                        <Form.Label className="fw-semibold mb-1">Emails par mois *</Form.Label>
                        <small className="text-muted d-block">Quota mensuel</small>
                      </div>
                    </div>
                    <div className="input-group has-validation">
                      <Form.Control
                        type="number"
                        min="1"
                        step="1"
                        value={emailsParMois || ""}
                        onChange={(e) => handleEmailsMoisChange(Number(e.target.value) || 0)}
                        placeholder="Ex: 3000"
                        className={`py-2 ${errors.emailsParMois ? 'is-invalid' : ''}`}
                      />
                      <span className="input-group-text bg-light">/mois</span>
                      {errors.emailsParMois && (
                        <div className="invalid-feedback">
                          {errors.emailsParMois}
                        </div>
                      )}
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <small className="text-muted">
                        <i className="bi bi-calendar3 me-1"></i>
                        Mensuel
                      </small>
                      <Badge bg="info" className="px-2">
                        {emailsParMois.toLocaleString()} email(s)
                      </Badge>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Emails par an */}
              <Col md={6} className="mb-3">
                <div className="card h-100 border-0 shadow-sm hover-shadow">
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      <div className="icon-container bg-success bg-opacity-10 p-2 rounded-circle me-3">
                        <i className="bi bi-calendar3 fs-5 text-success"></i>
                      </div>
                      <div>
                        <Form.Label className="fw-semibold mb-1">Emails par an</Form.Label>
                        <small className="text-muted d-block">Quota annuel</small>
                      </div>
                    </div>
                    <div className="input-group">
                      <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        value={emailsParAn || ""}
                        onChange={(e) => setEmailsParAn(Number(e.target.value) || 0)}
                        placeholder="Ex: 36000"
                        className="py-2"
                      />
                      <span className="input-group-text bg-light">/an</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <small className="text-muted">
                        <i className="bi bi-calendar me-1"></i> Annuel
                      </small>
                      <Badge bg="success" className="px-2">
                        {emailsParAn.toLocaleString()} email(s)
                      </Badge>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Description */}
                <Col md={6} className="mb-3">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-body">
                      <Form.Label className="fw-semibold d-flex align-items-center mb-3">
                        <i className="bi bi-text-paragraph me-2 text-muted"></i>
                        Description
                      </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={description}
                          onChange={handleDescriptionChange}
                          placeholder="Décrivez les avantages de cette offre..."
                          className="py-2"
                          maxLength={250}
                        />
                        <Form.Text className="text-muted mt-1 d-block text-end">
                          {description.length}/250 caractères
                        </Form.Text>


                      <Form.Text className="text-muted mt-2 d-block">
                        <i className="bi bi-info-circle me-1"></i>
                        Facultatif - apparaîtra dans les détails de l'offre
                      </Form.Text>
                    </div>
                  </div>
                </Col>
            </Row>
          </div>

          {/* Prix */}
          <div className="mb-4">
            <h6 className="fw-semibold mb-3 d-flex align-items-center">
              <i className="bi bi-currency-euro me-2 text-warning"></i>
              Tarification
            </h6>
            <Row>
              <Col md={6} className="mb-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center mb-3">
                      <div className="icon-container bg-warning bg-opacity-10 p-2 rounded-circle me-3">
                        <i className="bi bi-calendar-month fs-5 text-warning"></i>
                      </div>
                      <div>
                        <Form.Label className="fw-semibold mb-1">Prix mensuel</Form.Label>
                        <small className="text-muted d-block">Facturation mensuelle</small>
                      </div>
                    </div>
                    <div className="input-group has-validation">
                      <span className="input-group-text bg-white">
                        <i className="bi bi-cash-stack text-warning"></i>
                      </span>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={monthlyPrice || ""}
                        onChange={(e) => handleMonthlyPriceChange(Number(e.target.value) || 0)}
                        placeholder="0.00"
                        className={`${errors.monthlyPrice ? 'is-invalid' : ''}`}
                      />
                      <span className="input-group-text bg-white fw-semibold">€ / mois</span>
                      {errors.monthlyPrice && (
                        <div className="invalid-feedback">
                          {errors.monthlyPrice}
                        </div>
                      )}
                    </div>
                    <div className="mt-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          <i className="bi bi-graph-up me-1"></i>
                          Coût par email:
                        </small>
                        <Badge bg="warning" className="px-2 text-dark">
                          {coutParEmail > 0 ? coutParEmail.toFixed(4) : "0.0000"} €
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>

              <Col md={6} className="mb-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center mb-3">
                      <div className="icon-container bg-success bg-opacity-10 p-2 rounded-circle me-3">
                        <i className="bi bi-calendar3 fs-5 text-success"></i>
                      </div>
                      <div>
                        <Form.Label className="fw-semibold mb-1">Prix annuel</Form.Label>
                        <small className="text-muted d-block">Facturation annuelle</small>
                      </div>
                    </div>
                    <div className="input-group has-validation">
                      <span className="input-group-text bg-white">
                        <i className="bi bi-cash-coin text-success"></i>
                      </span>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        value={yearlyPrice || ""}
                        onChange={(e) => handleYearlyPriceChange(Number(e.target.value) || 0)}
                        placeholder="0.00"
                        className={`${errors.yearlyPrice ? 'is-invalid' : ''}`}
                      />
                      <span className="input-group-text bg-white fw-semibold">€ / an</span>
                      {errors.yearlyPrice && (
                        <div className="invalid-feedback">
                          {errors.yearlyPrice}
                        </div>
                      )}
                    </div>
                    <div className="mt-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          <i className="bi bi-percent me-1"></i>
                          Équivalent mensuel:
                        </small>
                        <Badge bg="success" className="px-2">
                          {monthlyEquivalent > 0 ? monthlyEquivalent.toFixed(2) : "0.00"} €
                        </Badge>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <small className="text-muted">
                          <i className="bi bi-graph-up me-1"></i>
                          Coût/email annuel:
                        </small>
                        <Badge bg="success" className="px-2">
                          {coutParEmailAnuel > 0 ? coutParEmailAnuel.toFixed(4) : "0.0000"} €
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* Récapitulatif */}
          <div className="card border-2" style={{ borderColor: "#4BB0C0" }}>
            <div className="card-body p-3">
              <h6 className="fw-semibold mb-3 d-flex align-items-center">
                <i className="bi bi-clipboard-check me-2"></i>
                Récapitulatif de l'offre
              </h6>
              <Row className="text-center">
                <Col xs={6} md={3} className="mb-3">
                  <div className="mb-2">
                    <i className="bi bi-calendar-day fs-4" style={{ color: "#4BB0C0" }}></i>
                  </div>
                  <div className="fw-bold fs-5">{emailsParJour.toLocaleString()}</div>
                  <small className="text-muted">Emails/jour</small>
                </Col>
                <Col xs={6} md={3} className="mb-3">
                  <div className="mb-2">
                    <i className="bi bi-calendar-month fs-4" style={{ color: "#4BB0C0" }}></i>
                  </div>
                  <div className="fw-bold fs-5">{emailsParMois.toLocaleString()}</div>
                  <small className="text-muted">Emails/mois</small>
                </Col>
                <Col xs={6} md={3} className="mb-3">
                  <div className="mb-2">
                    <i className="bi bi-calendar3 fs-4" style={{ color: "#4BB0C0" }}></i>
                  </div>
                  <div className="fw-bold fs-5">{emailsParAn.toLocaleString()}</div>
                  <small className="text-muted">Emails/an</small>
                </Col>
                <Col xs={6} md={3} className="mb-3">
                  <div className="mb-2">
                    <i className="bi bi-currency-euro fs-4" style={{ color: "#4BB0C0" }}></i>
                  </div>
                  <div className="fw-bold fs-5">
                    {monthlyPrice > 0 ? `${monthlyPrice.toFixed(2)}€` : "Gratuit"}
                  </div>
                  <small className="text-muted">/mois</small>
                </Col>
              </Row>
            </div>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="border-top-0 bg-light pt-3">
        <Button
          variant="outline-secondary"
          onClick={onClose}
          className="px-4 py-2 rounded-3 fw-semibold"
          style={{ minWidth: "120px" }}
          disabled={loading}
        >
          <i className="bi bi-x-circle me-2"></i>
          Annuler
        </Button>
        
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
          className="px-4 py-2 rounded-3 fw-semibold shadow-sm"
          style={{ 
            minWidth: "180px",
            background: "linear-gradient(135deg, #4BB0C0 0%, #3a92a2 100%)",
            border: "none"
          }}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Traitement...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-2"></i>
              {existingOffer ? "Mettre à jour" : "Créer l'offre"}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalOffre;
